// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getItems } from '@/items/api/get-items';
import { ItemTypeFilter } from '@/items/constants/item-types';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { without } from 'lodash-es';
import { useRef } from 'react';

const DEFAULT_LABEL = 'Item';

interface Props {
  /**
   * An array of strings representing the currently selected item IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   *
   * @default "Item"
   */
  label?: string;

  /**
   * Optional item type filter. If provided, only items of this type are shown.
   * @default 'BULK'
   */
  type?: ItemTypeFilter;

  /**
   * If true, include archived items in the list.
   * @default false
   */
  includeArchived?: boolean;

  /**
   * If true, only show physical items (excludes SERVICE items).
   * @default false
   */
  physicalOnly?: boolean;

  /**
   * Empty-state text shown inside the menu when there are no matching items.
   *
   * @default "No items found"
   */
  emptyLabel?: string;

  /**
   * A callback fired when the selected item IDs change.
   */
  onChange: (value: string[]) => void;
}

/**
 * A controlled filter menu for selecting zero or more items from the
 * Item Catalog API.
 */
export function ItemFilterMenu(props: Props) {
  // Default to BULK for backward compatibility
  const type = props.type ?? 'BULK';
  const includeArchived = props.includeArchived ?? false;

  const { data } = useSuspenseQuery({
    queryKey: ['item', type, { includeArchived }],
    queryFn: () => getItems({ type, archived: includeArchived }),
  });

  // Filter results based on physicalOnly prop
  const filteredResults = props.physicalOnly
    ? data.results.filter((item) => item.type !== 'SERVICE')
    : data.results;

  const menu = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);

  const label = props.label ?? DEFAULT_LABEL;
  const options = filteredResults
    .map(({ id, name, sku, isArchived }) => ({
      id,
      label: `${sku} (${name})`,
      isArchived,
      isActive: props.value.includes(id),
    }))
    .sort((a, b) => {
      // Sort archived items last, then alphabetically
      if (a.isArchived !== b.isArchived) {
        return a.isArchived ? 1 : -1;
      }
      return a.label.localeCompare(b.label);
    });

  const count = props.value.length ?? 0;
  const emptyLabel = props.emptyLabel ?? 'No items found';
  return (
    <>
      <FilterMenuButton ref={ref} count={count} onClick={menu.open}>
        {label}
      </FilterMenuButton>
      <Menu open={menu.isOpen} anchorEl={ref.current} onClose={menu.close}>
        {options.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {emptyLabel}
            </Typography>
          </MenuItem>
        ) : (
          options.map((option) => (
            <FilterMenuItem
              key={option.id}
              level={1}
              isActive={option.isActive}
              onChange={(isActive) => {
                if (isActive) {
                  const value = [...props.value, option.id];

                  return props.onChange(value);
                }
                props.onChange(without(props.value, option.id));
              }}
            >
              {option.label}
            </FilterMenuItem>
          ))
        )}
      </Menu>
    </>
  );
}
